import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  ReactNode,
  KeyboardEvent,
  MouseEvent,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import HTTP from '../../services/http';

export type Suggestion = {
  id: string;
  formattedSuggestion: any;
  placeId?: string;
  sessionToken?: string;
};

type Status = 'idle' | 'loading' | 'success' | 'error';

type Props<Raw = any> = {
  /** Controlled input value */
  value: string;
  /** Update controlled input value */
  onChange: (val: string) => void;
  /** Called when a suggestion is selected (mouse or keyboard) */
  onSelect: (suggestion: Suggestion, event?: MouseEvent | KeyboardEvent) => void | Promise<void>;
  /** Map your endpoint’s raw item to our Suggestion object */
  transformSuggestion: (raw: Raw) => Suggestion;

  /** UX controls */
  minLength?: number; // default: 2
  debounceMs?: number; // default: 250
  maxSuggestions?: number; // default: 8

  onSessionStart?: (token: string) => void;
  onSessionEnd?: (reason: 'select' | 'clear' | 'abort' | 'blur' | 'manual') => void;

  /** Render-prop child, same shape as react-places-autocomplete */
  children: (args: {
    getInputProps: (
      userProps?: React.InputHTMLAttributes<HTMLInputElement>,
    ) => React.InputHTMLAttributes<HTMLInputElement>;
    suggestions: Suggestion[];
    getSuggestionItemProps: (
      suggestion: Suggestion,
      userProps?: React.HTMLAttributes<HTMLElement>,
    ) => React.HTMLAttributes<HTMLElement>;
    loading: boolean;
    status: Status;
    activeIndex: number;
    clearSuggestions: () => void;
    error?: Error;
  }) => ReactNode;
};

/** Simple in-memory cache (FIFO) */
function createCache(max = 30) {
  const map = new Map<string, Suggestion[]>();
  return {
    get: (k: string) => map.get(k),
    set: (k: string, v: Suggestion[]) => {
      if (map.size >= max) {
        const firstKey = map.keys().next().value;
        if (firstKey) map.delete(firstKey);
      }
      map.set(k, v);
    },
    clear: () => map.clear(),
  };
}

const defaultTransform = (raw: any): Suggestion => {
  return {
    id: raw.id || raw.place_id || JSON.stringify(raw),
    formattedSuggestion: { mainText: raw.mainText, secondaryText: raw.secondaryText },
    placeId: raw.placeId,
  };
};

export function CustomPlacesAutocomplete<Raw = any>({
  value,
  onChange,
  onSelect,
  transformSuggestion = defaultTransform,
  minLength = 2,
  debounceMs = 250,
  maxSuggestions = 8,
  onSessionStart,
  onSessionEnd,
  children,
}: Props<Raw>) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<Error | undefined>(undefined);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [isFocused, setIsFocused] = useState(false);
  const [sessionToken, setSessionToken] = useState<string | undefined>(() => uuidv4());
  const suppressSuggestionsRef = useRef(false);
  const cacheRef = useRef(createCache(40));
  const abortRef = useRef<AbortController | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
      abortRef.current?.abort();
    };
  }, []);

  // Debounced querying
  useEffect(() => {
    if (suppressSuggestionsRef.current) {
      suppressSuggestionsRef.current = false;
      return;
    }
    if (!isFocused) return;
    const q = value.trim();
    if (q.length < minLength) {
      setSuggestions([]);
      setStatus('idle');
      setError(undefined);
      setActiveIndex(-1);
      return;
    }

    const cached = cacheRef.current.get(q);
    if (cached) {
      setSuggestions(cached.slice(0, maxSuggestions).map(item => ({ ...item, sessionToken })));
      setStatus('success');
      setError(undefined);
      setActiveIndex(-1);
      return;
    }

    // Notify session start (optional)
    onSessionStart?.(sessionToken || 'session-unknown');

    setStatus('loading');
    setError(undefined);

    // debounce
    const timer = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const suggestion_list = await HTTP.get(
          `/config/address-suggestions?q=${encodeURIComponent(q)}&sessionToken=${sessionToken}`,
          { signal: controller.signal },
        );
        const mapped = suggestion_list.data.suggestions
          .map(transformSuggestion)
          .filter(Boolean)
          .map((item: any) => ({ ...item, sessionToken }));
        cacheRef.current.set(q, mapped);
        if (!isMounted.current) return;
        setSuggestions(mapped.slice(0, maxSuggestions));
        setStatus('success');
        setActiveIndex(-1);
      } catch (err: any) {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
        if (!isMounted.current) return;
        setStatus('error');
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    }, debounceMs);

    return () => {
      clearTimeout(timer);
      abortRef.current?.abort();
    };
  }, [value, minLength, debounceMs, maxSuggestions, isFocused]);

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setActiveIndex(-1);
    setStatus('idle');
  }, []);

  const selectAtIndex = useCallback(
    (idx: number, event?: MouseEvent | KeyboardEvent) => {
      const item = suggestions[idx];
      if (!item) return;
      onSelect({ ...item, sessionToken }, event);
      suppressSuggestionsRef.current = true;
      clearSuggestions();
      onSessionEnd?.('select');
    },
    [suggestions, onSelect, clearSuggestions, onSessionEnd, sessionToken],
  );

  /** Keyboard handling for the input */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (!['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(e.key)) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex(i => (i + 1) % Math.max(1, suggestions.length));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(i => (i <= 0 ? suggestions.length - 1 : i - 1));
      } else if (e.key === 'Enter') {
        if (activeIndex >= 0 && activeIndex < suggestions.length) {
          e.preventDefault();
          selectAtIndex(activeIndex, e);
        }
      } else if (e.key === 'Escape') {
        clearSuggestions();
        onSessionEnd?.('manual');
      }
    },
    [activeIndex, suggestions.length, selectAtIndex, clearSuggestions, onSessionEnd],
  );

  /** Merge user input props while preserving handlers */
  const getInputProps = useCallback(
    (userProps: React.InputHTMLAttributes<HTMLInputElement> = {}) => {
      const {
        onChange: userOnChange,
        onKeyDown: userOnKeyDown,
        onFocus: userOnFocus,
        onBlur: userOnBlur,
        value: userValue, // ignored, we use controlled value
        ...rest
      } = userProps;

      return {
        value,
        'onChange': (e: React.ChangeEvent<HTMLInputElement>) => {
          suppressSuggestionsRef.current = false;
          onChange(e.target.value);
          userOnChange?.(e);
        },
        'onKeyDown': (e: KeyboardEvent<HTMLInputElement>) => {
          handleKeyDown(e);
          userOnKeyDown?.(e);
        },
        'onFocus': (e: React.FocusEvent<HTMLInputElement>) => {
          setIsFocused(true);
          suppressSuggestionsRef.current = true;
          clearSuggestions();
          userOnFocus?.(e);
        },
        'onBlur': (e: React.FocusEvent<HTMLInputElement>) => {
          // Delay to allow click events on suggestions
          setTimeout(() => {
            setIsFocused(false);
            clearSuggestions();
            onSessionEnd?.('blur');
          }, 100);
          userOnBlur?.(e);
        },
        'role': 'combobox',
        'aria-expanded': suggestions.length > 0,
        'aria-autocomplete': 'list',
        'aria-controls': 'places-suggestions',
        ...rest,
      } as React.InputHTMLAttributes<HTMLInputElement>;
    },
    [value, onChange, handleKeyDown, suggestions.length, clearSuggestions, onSessionEnd],
  );

  /** Props for each suggestion row (mouse & ARIA) */
  const getSuggestionItemProps = useCallback(
    (suggestion: Suggestion, userProps: React.HTMLAttributes<HTMLElement> = {}) => {
      const index = suggestions.findIndex(s => s.id === suggestion.id);
      const active = index === activeIndex;

      const { onMouseDown: upMouseDown, onClick: upClick, ...rest } = userProps;

      return {
        'key': suggestion.placeId,
        'id': `places-suggestion-${index}`,
        'role': 'option',
        'aria-selected': active,
        'onMouseDown': (e: React.MouseEvent<HTMLElement>) => {
          // Select on mouse down so the list closes immediately.
          e.preventDefault();
          selectAtIndex(index, e);
          upMouseDown?.(e);
        },
        'onClick': (e: React.MouseEvent<HTMLElement>) => {
          upClick?.(e);
        },
        ...rest,
      } as React.HTMLAttributes<HTMLElement>;
    },
    [suggestions, activeIndex, selectAtIndex],
  );

  return (
    <>
      {children({
        getInputProps,
        suggestions,
        getSuggestionItemProps,
        loading: status === 'loading',
        status,
        activeIndex,
        clearSuggestions,
        error,
      })}
    </>
  );
}

export async function customPlacesGeocode(raw: any): Promise<any> {
  const { sessionToken, placeId } = raw;
  if (!placeId || !sessionToken) throw new Error('placeId and sessionToken required for geocoding');
  const qParams = new URLSearchParams();
  qParams.set('sessionToken', sessionToken);
  qParams.set('placeId', placeId);

  const endpointUrl = `/config/address-geocode?${qParams.toString()}`;

  const res = await HTTP.get(endpointUrl, {});
  return res.data;
}
